import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import microSimService from '../../services/microSimService';
import { bandOf } from './microSimBands';

/**
 * "Your practice results" — a participant's own finished micro simulations in this
 * session (D3: only their own scores). Renders nothing when there are none, or when
 * the device has no signed participant token for this session.
 */
const MyPracticeResults = ({ sessionId }) => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    let alive = true;
    microSimService.completed(sessionId)
      .then((res) => { if (alive) setRuns(res.runs || []); })
      .catch(() => { /* no token on this device, or nothing to show */ });
    return () => { alive = false; };
  }, [sessionId]);

  if (!runs.length) return null;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 sm:p-6">
      <h2 className="text-base font-bold text-neutral-900">Your practice results</h2>
      <p className="mt-0.5 text-sm text-neutral-500">From the micro simulations you played in this session.</p>
      <div className="mt-4 space-y-3">
        {runs.map((run) => (
          <button
            key={run.runId}
            type="button"
            onClick={() => navigate(`/session/${sessionId}/micro-sim/${run.runId}?view=report`)}
            className="block w-full rounded-xl border border-neutral-200 p-4 text-left transition-all hover:border-primary-300 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wide text-violet-600">{run.theme}</p>
                <p className="font-bold text-neutral-900">{run.title}</p>
                {run.partnerName && <p className="truncate text-xs text-neutral-500">Practised with {run.partnerName}</p>}
              </div>
              <span className="shrink-0 text-sm font-semibold text-primary-600">Open →</span>
            </div>
            {/* Mini skill strip: one segment per skill, coloured by band */}
            <div className="mt-3 flex gap-1" aria-hidden="true">
              {run.skills.map((s) => (
                <div key={s.key} className={`h-2 flex-1 rounded-full ${bandOf(s.score).bar}`} title={`${s.label}: ${s.score ?? '–'}%`} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
              {run.strongest && <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">Strongest: {run.strongest}</span>}
              {run.practiceFocus && <span className="rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-700">Focus: {run.practiceFocus}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

MyPracticeResults.propTypes = { sessionId: PropTypes.string.isRequired };

export default MyPracticeResults;
